/**
 * Un exercice servi hors séance, et ce que le serveur en dit.
 *
 * Extrait de `RevisionScreen` le 3 septembre 2026, quand l'écran du jour est
 * arrivé : les deux écrans tirent un exercice ailleurs, mais ce qu'ils en font
 * ensuite est le même geste — un énoncé, une réponse, un verdict en trois blocs
 * qui ne se ressemblent pas. Recopié, ce bloc aurait divergé au premier
 * ajustement, et la divergence se serait vue chez l'enfant avant de se voir
 * ici : la révision aurait gardé l'explication de l'erreur que le travail du
 * jour aurait perdue, sans qu'aucun test ne s'en aperçoive.
 *
 * **Ce composant ne tire rien.** Il reçoit l'exercice déjà tiré, parce que le
 * tirage n'est pas le même de part et d'autre (`/revision/next` contre
 * `/du-jour/next`) et que c'est précisément la seule différence.
 *
 * **La correction ne se calcule pas ici** (invariant §14.3). Elle vient de
 * `POST /attempt` : ce qui est affiché est ce que le serveur a répondu, et rien
 * n'est rédigé sur l'appareil.
 *
 * Les classes CSS gardent le préfixe `revision__` : elles existent, elles sont
 * testées, et les renommer pour l'esthétique du nom aurait changé une feuille
 * de style au milieu d'un déplacement de code.
 */

import { useEffect, useState } from "react";

import { call } from "@/api/client";
import type { CorrectionOut, ExerciseOut } from "@/api/schema";
import { ExercisePanel } from "@/child/ExercisePanel";
import { MilestoneNotice } from "@/child/MilestoneBanner";
import { RenduAuParent } from "@/child/RenduAuParent";
import { MILESTONE_SENTENCE } from "@/config/wording";
import { imageDeLEnonce, imagesDeLEnonce } from "@/ui/EnonceImage";
import { PasEncoreVu } from "@/ui/PasEncoreVu";
import { TexteMathematique } from "@/ui/TexteMathematique";
import { Button, Card } from "@/ui/primitives";

/**
 * Le titre de la carte qui suit l'envoi — le seul mot que cet écran choisit.
 *
 * **Sans verdict, pas de verdict** : le serveur rend `correct: null` pendant un
 * test de positionnement (SPEC §5.7, décidé le 29 septembre 2026), dont l'écran
 * promet « personne ne verra de résultat — pas même toi ». Un « Pas encore »
 * affiché là serait le résultat, question par question. Et une réponse que le
 * serveur n'a pas su lire n'est ni juste ni fausse : elle est à reformuler.
 */
export function titreDuVerdict(verdict: Pick<CorrectionOut, "correct" | "parsed">): string {
  if (verdict.correct === null || verdict.correct === undefined) return "Réponse notée";
  if (verdict.parsed === false) return "À reformuler";
  return verdict.correct ? "C'est juste" : "Pas encore";
}

export function ExerciceEtCorrige({
  item,
  matiere,
  retour,
  suivant,
  libelleRetour = "← Revenir à la liste",
  libelleSuivant = "Un autre",
  libelleChanger = "Changer de sujet",
  papier = true,
}: {
  item: ExerciseOut;
  matiere: string;
  /** Sortir de l'exercice, vers la liste d'où il vient. */
  retour: () => void;
  /** En tirer un autre sur la même compétence. */
  suivant: () => void;
  libelleRetour?: string;
  libelleSuivant?: string;
  libelleChanger?: string;
  /**
   * Proposer « C'est fait sur mon cahier » quand le serveur dit que l'exercice
   * peut se rendre sur papier (`sur_papier`). Faux pour le positionnement : ses
   * réponses sont observées en fin de test, et un verdict de parent écrirait
   * une observation au milieu.
   */
  papier?: boolean;
}) {
  const [verdict, setVerdict] = useState<CorrectionOut | null>(null);
  const [surPapier, setSurPapier] = useState(false);

  // Un nouvel exercice efface le verdict du précédent. Sans cette ligne, un
  // enfant qui enchaîne verrait la correction d'avant sous l'énoncé d'après.
  useEffect(() => {
    setVerdict(null);
    setSurPapier(false);
  }, [item.exercise_id]);

  // **« On n'a pas encore vu ça en classe », des deux côtés de la branche.**
  // Demandé par Hedi le 7 septembre 2026. La frontière du programme retombe en
  // dernier recours sur un découpage théorique de l'année que personne n'a
  // signé, et l'enfant est le seul à savoir que la leçon n'a pas eu lieu.
  //
  // Rendu ici plutôt que dans chacun des deux composants : un tracé se refuse
  // exactement comme un calcul, et recopier le bouton l'aurait fait disparaître
  // d'un côté au premier ajustement — la raison même pour laquelle ce fichier
  // existe.
  const refus = <PasEncoreVu key={item.exercise_id} exerciseId={item.exercise_id} />;

  // **Un tracé ne se tape pas.** Un exercice à validation parentale n'a pas de
  // champ de réponse, pas de verdict et pas de corrigé sur cet appareil : il se
  // fait sur le cahier, se photographie, et c'est un parent qui coche
  // (`exercise` §11). Le branchement est ici plutôt que dans chaque écran
  // appelant, pour la raison qui a fait naître ce composant — recopié trois
  // fois, il aurait divergé au premier ajustement.
  // **Un sujet à images peut aussi se faire sur le cahier** — Hedi, le
  // 26 septembre 2026 : « Je veux pouvoir la corriger en tant que parent et
  // renseigner si c'est correct ou pas. » L'enfant choisit ; la saisie corrigée
  // par le modèle reste possible tant qu'il n'a rien envoyé. Le corrigé, lui,
  // n'arrive jamais sur cet appareil par ce chemin : c'est un parent qui juge.
  const peutSurPapier = papier && Boolean(item.sur_papier) && !verdict;
  if (item.restitution === "parent_validated" || (surPapier && peutSurPapier)) {
    return (
      <>
        <RenduAuParent
          key={item.exercise_id}
          item={item}
          retour={retour}
          suivant={suivant}
          libelleRetour={libelleRetour}
          libelleSuivant={libelleSuivant}
          revenirALaSaisie={
            item.restitution === "parent_validated" ? undefined : () => setSurPapier(false)
          }
        />
        {refus}
      </>
    );
  }

  return (
    <>
      {/* **Une sortie, toujours visible, et en haut.** Un enfant qui ouvre un
          exercice trop difficile, ou qui s'est trompé de sujet, n'a pas d'autre
          issue que le bouton « précédent » du navigateur — lequel, dans une PWA
          installée, n'existe pas. Se retrouver enfermé sur un exercice qu'on ne
          sait pas faire est le meilleur moyen de fermer l'application et de ne
          pas la rouvrir.

          Elle est posée avant l'énoncé plutôt qu'après : au moment où l'on
          renonce, on ne fait pas défiler la page jusqu'en bas pour chercher la
          porte. */}
      <div className="revision__retour">
        <Button variant="quiet" onClick={retour}>
          {libelleRetour}
        </Button>
      </div>
      <ExercisePanel
        key={item.exercise_id}
        exercise={{
          eventId: 0,
          statement: item.statement,
          // La *forme* attendue, jamais la réponse : `saisie` ne porte pas
          // `canonical`, et c'est ce qui permet de l'envoyer à l'enfant.
          answer: item.saisie as unknown as Record<string, unknown>,
          hints: item.hints ?? [],
          image: imageDeLEnonce(item.statement_image_url),
          images: imagesDeLEnonce(item.statement_image_urls, item.statement_image_url),
          parascolaire: item.parascolaire ?? null,
        }}
        hints={[]}
        matiere={matiere}
        onSubmit={async (reponse) => {
          const rendu = await call("submitAttempt", {
            params: { exercise_id: item.exercise_id },
            body: reponse,
          });
          setVerdict(rendu);
          return rendu;
        }}
      />
      {/* Sous l'exercice et jamais à côté du bouton « Envoyer » : une porte de
          sortie posée à hauteur de la réponse devient la réponse. Elle
          disparaît dès qu'un verdict est tombé — à ce moment-là l'exercice a
          été fait, et « on ne l'a pas vu » ne veut plus rien dire. */}
      {peutSurPapier ? (
        <div className="revision__suite">
          <Button variant="quiet" onClick={() => setSurPapier(true)}>
            C'est fait sur mon cahier
          </Button>
        </div>
      ) : null}
      {verdict ? null : refus}
      {/* Le palier que cette réponse vient de faire franchir, hors séance
          (web-pwa §12). Décidé par le serveur, comme le verdict : l'écran ne
          compare aucun état, il affiche ce qu'on lui rend. Il reste jusqu'à
          l'exercice suivant, qui efface le verdict et lui avec. */}
      {verdict?.jalon ? (
        <MilestoneNotice
          competency={verdict.jalon.competency_label ?? ""}
          sentence={MILESTONE_SENTENCE[verdict.jalon.state]}
        />
      ) : null}
      {verdict ? (
        <Card title={titreDuVerdict(verdict)}>
          {verdict.correct === null && !verdict.message ? (
            <p className="muted small">Question suivante quand tu veux.</p>
          ) : null}
          {/* Le message vient du serveur : lui seul sait pourquoi. Rien n'est
              rédigé ici, sinon l'interface commenterait une correction qu'elle
              n'a pas faite. */}
          {verdict.message ? <p>{verdict.message}</p> : null}
          {/* **Deux blocs, deux moments, et ils ne se ressemblent pas** —
              demandé par Hedi le 28 août 2026 : « sur l'UI l'explication de
              l'erreur et la correction doivent être présentées différemment ».

              L'explication arrive à **chaque** réponse fausse et ne dit jamais
              ce qu'il fallait écrire : elle ouvre l'essai suivant. La
              correction n'arrive qu'au bout de plusieurs tentatives et le clôt.
              Les présenter du même trait laissait croire qu'il n'y avait plus
              rien à tenter.

              Ce qui les sépare est donc leur sens, pas leur ordre : l'un est
              une piste, l'autre une réponse. Le premier bloc porte le filet
              d'accent en marge, le second l'aplat de papier du corrigé.

              Aucune des deux conditions n'est décidée ici. Le serveur envoie ce
              qu'il envoie ; une condition recopiée dans cet écran serait une
              seconde porte du contrat socratique, dans le seul endroit de
              l'application que l'enfant peut ouvrir avec les outils de
              développement. */}
          {verdict.explication ? (
            <div className="piste">
              <h3 className="piste__titre">Où ça a dérapé</h3>
              <TexteMathematique className="piste__texte">{verdict.explication}</TexteMathematique>
            </div>
          ) : null}
          {verdict.corrige ? (
            <div className="debrief">
              <h3 className="debrief__titre">La correction</h3>
              <TexteMathematique className="debrief__texte">{verdict.corrige}</TexteMathematique>
            </div>
          ) : null}
          {/* **En bas, toujours, et en marge de tout le reste** — demandé par
              Hedi le 28 août 2026 : « quand il y a des fautes de français dans
              la réponse, note ça en bas de la correction et de la réponse même
              si ce n'est pas la compétence évaluée ».

              C'est le troisième bloc, et il ne ressemble ni à la piste ni au
              corrigé, parce qu'il ne parle pas de l'exercice. Il accompagne
              aussi bien une réponse juste qu'une réponse fausse : une copie
              exacte écrite avec des fautes est exactement le cas que la
              demande vise, et c'est celui où rien n'était dit.

              Il vient en dernier pour la même raison qu'il est discret :
              l'enfant a travaillé sur autre chose. */}
          {verdict.langue ? (
            <div className="langue">
              <h3 className="langue__titre">Au passage, le français</h3>
              <p className="langue__texte">{verdict.langue}</p>
            </div>
          ) : null}
          <div className="revision__suite">
            <Button onClick={suivant}>{libelleSuivant}</Button>
            <Button variant="quiet" onClick={retour}>
              {libelleChanger}
            </Button>
          </div>
        </Card>
      ) : null}
    </>
  );
}
